import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { magicQuoteApi } from '../../api/endpoints.ts';
import { Button } from '../ui.tsx';
import type { MagicQuoteObservation } from '@shared/types/index.ts';
import { AUTO_CONFIRM_CONFIDENCE, REVIEW_CONFIDENCE_THRESHOLD } from '@shared/magic-quote.ts';

/**
 * The claims the pipeline made, each with what it is based on and what a
 * person can do about it.
 *
 * Three affordances, and they are the whole contract Magic Quote offers an
 * estimator: **confirm** (yes, that is what happened), **reject** (no, and
 * stop pricing it), and **show me** (open the photo, or the second of the
 * recording where the customer said it). The third is what makes the first
 * two honest — an estimator confirming claims they cannot check is just
 * clicking through, and a review screen that invited that would be worse than
 * none.
 *
 * The internal/customer switch sits here too, because the decision it makes
 * belongs beside the words it applies to: this line is about to be either an
 * internal note or something a stranger reads about their own home.
 */
export default function ObservationList({
  sessionId,
  observations,
  canEdit,
  onShowEvidence,
}: {
  sessionId: string;
  observations: MagicQuoteObservation[];
  canEdit: boolean;
  onShowEvidence: (evidence: { captureId: string; offsetSeconds: number | null }) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // One ruling over the whole list. Reading a list and agreeing with it is a
  // single decision; charging forty clicks for it is how a review screen
  // becomes something people tap through without reading.
  //
  // Each sweep names only the rows it is about — the unruled ones when
  // confirming, the confirmed ones when taking it back. Sending every id
  // would make "confirm all" resurrect a claim the estimator had thrown out
  // and put that work back in the price, which is the exact opposite of what
  // pressing it means. (The server enforces the same rule; this is so the
  // count on the button matches what the button does.)
  const unruled = observations.filter((observation) => observation.reviewStatus === 'unreviewed');
  const confirmed = observations.filter((observation) => observation.reviewStatus === 'confirmed');

  // Whether this list is asking for anything.
  //
  // The same component draws both lists on the review screen, and only one of
  // them is a question. Above the other — the findings that were accepted for
  // the estimator — a "Confirm all 23" bar and a running "14 of 23 confirmed"
  // tally rebuild exactly the checklist those findings were taken off, and
  // contradict the sentence above them saying they are done. A list nobody is
  // being asked about gets no sweep and no tally.
  const asking = observations.some((observation) => observation.needsReview);

  const reviewAll = useMutation({
    mutationFn: (reviewStatus: 'unreviewed' | 'confirmed') =>
      magicQuoteApi.reviewObservations(sessionId, {
        observationIds: (reviewStatus === 'confirmed' ? unruled : confirmed).map((observation) => observation.id),
        reviewStatus,
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['magic-quote', sessionId] }),
  });

  if (observations.length === 0) {
    return <p className="text-sm text-[color:var(--color-muted)]">{t('magicQuote.observations.empty')}</p>;
  }

  return (
    <>
      {/* What the two buttons on every row actually do to the quote. The
          screen was full of Confirm and Reject and said nowhere what either
          one changed — so the honest reading of "confirm" was "acknowledge
          that I have seen this", which is not what it means at all. */}
      {canEdit && asking ? (
        <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('magicQuote.observations.rulingHint')}</p>
      ) : null}

      {canEdit && asking && observations.length > 1 ? (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-3 py-2">
          <Button
            variant="ghost"
            busy={reviewAll.isPending}
            disabled={unruled.length === 0}
            onClick={() => reviewAll.mutate('confirmed')}
          >
            {t('magicQuote.observations.confirmAll', { count: unruled.length })}
          </Button>
          <Button
            variant="ghost"
            busy={reviewAll.isPending}
            disabled={confirmed.length === 0}
            onClick={() => reviewAll.mutate('unreviewed')}
          >
            {t('magicQuote.observations.unconfirmAll', { count: confirmed.length })}
          </Button>
          <span className="ml-auto text-xs font-medium text-[color:var(--color-muted)]">
            {t('magicQuote.observations.confirmedCount', {
              confirmed: confirmed.length,
              total: observations.length,
            })}
          </span>
        </div>
      ) : null}

      <ul className="flex flex-col gap-3">
        {observations.map((observation) => (
          <li key={observation.id}>
            <ObservationRow
              sessionId={sessionId}
              observation={observation}
              canEdit={canEdit}
              onShowEvidence={onShowEvidence}
            />
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * One ruling: what it is now, and how to take it back.
 *
 * Two things have to be true at once, and the first attempt only managed one
 * of them. Fixed labels made a column of these scannable — the filled ones are
 * the decisions — but they also made the filled button read as "Confirm", so
 * pressing it looked like confirming something already confirmed rather than
 * undoing it. A label that flips to "Unconfirm" has the opposite fault: it
 * says what the press does and stops saying what the row IS, which is what
 * made the original unreadable.
 *
 * So the pressed button says both. It becomes the state — "Confirmed", past
 * tense, ticked — and carries an ✕, which is the one mark that means "clear
 * this" everywhere a chip has ever been removable. The accessible name spells
 * it out for anyone who cannot see either.
 */
function RulingButton({
  label,
  stateLabel,
  undoLabel,
  pressed,
  busy,
  tone,
  onClick,
}: {
  /** The verb, shown when this is not the current ruling: "Confirm". */
  label: string;
  /** The state, shown when it is: "Confirmed". */
  stateLabel: string;
  /** What pressing it now does, for the accessible name and the tooltip. */
  undoLabel: string;
  pressed: boolean;
  busy: boolean;
  tone: 'brand' | 'danger';
  onClick: () => void;
}) {
  const filled =
    tone === 'brand'
      ? 'border-[color:var(--color-brand)] bg-[color:var(--color-brand)] text-white hover:bg-[color:var(--color-brand-strong)]'
      : 'border-[color:var(--color-danger)] bg-[color:var(--color-danger)] text-white hover:opacity-90';
  const outline =
    tone === 'brand'
      ? 'border-[color:var(--color-line)] bg-white text-[color:var(--color-ink)] hover:bg-[color:var(--color-surface)]'
      : 'border-[color:var(--color-line)] bg-white text-[color:var(--color-danger)] hover:bg-[color:var(--color-danger-soft)]';

  return (
    <button
      type="button"
      aria-pressed={pressed}
      aria-label={pressed ? undoLabel : label}
      title={pressed ? undoLabel : label}
      disabled={busy}
      onClick={onClick}
      className={`inline-flex min-h-9 items-center gap-1.5 rounded-md border px-3 py-1 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${pressed ? filled : outline}`}
    >
      {/* A tick on the pressed one, so the state survives being read in
          grayscale or by somebody who does not parse the color (spec §27). */}
      {pressed ? (
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          aria-hidden="true"
        >
          <path d="M4 12.5l5.5 5.5L20 6.5" />
        </svg>
      ) : null}
      {pressed ? stateLabel : label}
      {/* The undo affordance. On a phone there is no hover and no tooltip, so
          the only way "press this to take it back" can be known is by being
          drawn. */}
      {pressed ? (
        <span
          className="-mr-1 ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-white/25"
          aria-hidden="true"
        >
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
            <path d="M5 5l14 14M19 5L5 19" />
          </svg>
        </span>
      ) : null}
    </button>
  );
}

/**
 * How sure the pipeline is, as a chip that carries its own weight.
 *
 * Three bands rather than a raw number alone: a percentage is precise and
 * meaningless at a glance, and the only distinction an estimator acts on is
 * "stated outright" / "read off a photo" / "guessed". The number stays, so
 * the chip is never the whole story.
 */
function ConfidenceChip({ confidence }: { confidence: number }) {
  const { t } = useTranslation();
  const percent = Math.round(confidence * 100);
  const tone =
    confidence >= AUTO_CONFIRM_CONFIDENCE
      ? 'border-[color:var(--color-brand)]/40 bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand-strong)]'
      : confidence >= REVIEW_CONFIDENCE_THRESHOLD
        ? 'border-[color:var(--color-line)] text-[color:var(--color-muted)]'
        : 'border-[color:var(--color-warning)]/50 bg-[color:var(--color-warning-soft)] text-[color:var(--color-warning)]';
  return <span className={`badge ${tone}`}>{t('magicQuote.observations.confidence', { percent })}</span>;
}

function ObservationRow({
  sessionId,
  observation,
  canEdit,
  onShowEvidence,
}: {
  sessionId: string;
  observation: MagicQuoteObservation;
  canEdit: boolean;
  onShowEvidence: (evidence: { captureId: string; offsetSeconds: number | null }) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  /** Whether this row's controls have been asked for. See below. */
  const [open, setOpen] = useState(false);

  const review = useMutation({
    mutationFn: (body: {
      reviewStatus?: 'unreviewed' | 'confirmed' | 'corrected' | 'rejected';
      visibility?: 'internal' | 'customer';
    }) => magicQuoteApi.reviewObservation(sessionId, observation.id, body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['magic-quote', sessionId] }),
  });

  const rejected = observation.reviewStatus === 'rejected';
  const confirmed = observation.reviewStatus === 'confirmed';
  /** Whether the ruling buttons are on screen, and therefore stating the status themselves. */
  const ruling = canEdit && (observation.needsReview || open);

  return (
    <div
      className={[
        'rounded-xl border p-4 transition-colors',
        rejected
          ? 'border-[color:var(--color-line)] bg-[color:var(--color-surface)]'
          : confirmed
            ? 'border-[color:var(--color-brand)]/30 bg-white'
            : // Unruled and hedged is the one state worth coloring: it is the
              // pile the review screen exists to get through.
              observation.needsReview
              ? 'border-[color:var(--color-warning)]/40 bg-[color:var(--color-warning-soft)]/40'
              : 'border-[color:var(--color-line)] bg-white',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-3">
        {/* `min-w-0`, because a flex item will not shrink below its longest
            word by default, and the words here are the model's: a finding
            naming a product or a URL with nothing to break on would push this
            row past the screen — and a row wider than the screen widens the
            whole document, which the browser then draws shrunk to fit (spec
            §31.10). The base layer breaks the word; this is what lets the box
            get narrow enough for the break to happen. */}
        <p
          className={
            rejected
              ? 'min-w-0 text-sm line-through text-[color:var(--color-muted)]'
              : 'min-w-0 text-sm leading-snug font-semibold text-[color:var(--color-ink)]'
          }
        >
          {observation.label}
        </p>
        <span className="badge shrink-0 border-[color:var(--color-line)] text-[color:var(--color-muted)]">
          {t(`magicQuote.observationType.${observation.type}`)}
        </span>
      </div>

      {observation.detail ? (
        <p className="mt-1.5 text-sm text-[color:var(--color-muted)]">{observation.detail}</p>
      ) : null}

      {/* Three facts, three chips. As a run-on sentence of gray text they read
          as one caption nobody parses; as chips the estimator can find the one
          they are scanning for — how sure, who sees it, has anyone said. */}
      <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
        <ConfidenceChip confidence={observation.confidence} />
        <span className="badge border-[color:var(--color-line)] text-[color:var(--color-muted)]">
          {t(`magicQuote.visibility.${observation.visibility}`)}
        </span>
        {/* Always stated, including "not reviewed". A row that showed its
            ruling only once it had one left the estimator unable to tell
            "nobody has looked at this" from "somebody looked and agreed",
            which is the one distinction this screen exists to make.
            
            Dropped when the buttons below are showing and already say it:
            "Confirmed" as a chip AND as the button beside it is the same fact
            twice, and the second one reads as a different fact until you look
            hard. A row nobody can edit still needs the chip, and 'corrected'
            is a state the two buttons cannot express at all.

            And a row whose buttons are COLLAPSED needs it back, which is what
            collapsing them broke: a rejected finding is struck through and
            left out of the quote, and with the pressed "Removed" button hidden
            the strike-through was the only thing saying so. A screen reader
            announces the text and not the paint, so an estimator using one
            heard an excluded finding exactly like a live one and would have
            had to open every row to find out. */}
        {!canEdit ||
        !ruling ||
        observation.reviewStatus === 'corrected' ||
        observation.reviewStatus === 'unreviewed' ? (
          <span
            className={[
              'badge',
              confirmed
                ? 'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand-strong)]'
                : rejected
                  ? 'border-[color:var(--color-danger)] text-[color:var(--color-danger)]'
                  : 'border-[color:var(--color-line)] text-[color:var(--color-muted)]',
            ].join(' ')}
          >
            {t(`magicQuote.reviewStatus.${observation.reviewStatus}`)}
          </span>
        ) : null}
      </div>

      {observation.evidence.length > 0 ? (
        <div className="mt-2.5 flex flex-wrap gap-2">
          {observation.evidence.map((source) => (
            <button
              key={`${source.captureId}-${source.offsetSeconds ?? 'x'}`}
              type="button"
              className="inline-flex items-center gap-1 rounded-full border border-[color:var(--color-brand)]/30 bg-[color:var(--color-brand-soft)] px-2.5 py-1 text-xs font-semibold text-[color:var(--color-brand-strong)] hover:bg-white"
              onClick={() => onShowEvidence({ captureId: source.captureId, offsetSeconds: source.offsetSeconds })}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                aria-hidden="true"
              >
                <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12z" />
                <circle cx="12" cy="12" r="2.6" />
              </svg>
              {source.offsetSeconds !== null
                ? t('magicQuote.observations.evidenceAt', { seconds: Math.round(source.offsetSeconds) })
                : t('magicQuote.observations.evidence')}
            </button>
          ))}
        </div>
      ) : null}

      {observation.evidence.some((source) => source.excerpt) ? (
        <blockquote className="mt-2.5 border-l-2 border-[color:var(--color-brand)]/40 pl-3 text-sm text-[color:var(--color-muted)] italic">
          {observation.evidence.find((source) => source.excerpt)?.excerpt}
        </blockquote>
      ) : null}

      {/*
        The controls are offered, not pushed.

        Every row used to carry a Confirm, a Reject and a visibility toggle,
        which turns a walkthrough's findings into a form with forty fields.
        The estimator is asked about the handful that decide what gets priced;
        the rest are things that HAPPENED — a dog, a side entrance, soap
        buildup — and the right posture for those is a list you read, with a
        way in if one of them is wrong. So the buttons stay one tap away
        instead of on screen forty times.
      */}
      {canEdit && !ruling ? (
        <div className="mt-3 border-t border-[color:var(--color-line)] pt-3">
          <button
            type="button"
            className="text-sm font-semibold text-[color:var(--color-brand-strong)] underline-offset-2 hover:underline"
            // Named, because a screen reader moving control to control hears
            // only the button. A list of findings each offering "Change" is a
            // list of identical buttons, and picking the right one means
            // walking the whole page linearly to work out which card you are
            // standing in.
            aria-label={t('magicQuote.observations.changeNamed', { label: observation.label })}
            onClick={() => setOpen(true)}
          >
            {t('magicQuote.observations.change')}
          </button>
        </div>
      ) : null}

      {ruling ? (
        <div className="mt-3 flex flex-wrap gap-2 border-t border-[color:var(--color-line)] pt-3">
          {/*
            Two buttons whose LABELS never change, one of which is visibly
            pressed. The pair used to be a single button that relabeled itself
            "Unconfirm" once pressed, which reads as a third option rather than
            as the current state: an estimator scanning the list saw "Confirm"
            on one row and "Unconfirm" on the next and could not tell which of
            them had been agreed with. Now the ruling is the highlighted
            button, taking it back is pressing that button again, and
            `aria-pressed` says the same thing to a screen reader.
          */}
          <RulingButton
            label={t('magicQuote.observations.confirm')}
            stateLabel={t('magicQuote.reviewStatus.confirmed')}
            undoLabel={t('magicQuote.observations.unconfirm')}
            pressed={confirmed}
            busy={review.isPending}
            tone="brand"
            onClick={() => review.mutate({ reviewStatus: confirmed ? 'unreviewed' : 'confirmed' })}
          />
          <RulingButton
            label={t('magicQuote.observations.reject')}
            stateLabel={t('magicQuote.reviewStatus.rejected')}
            undoLabel={t('magicQuote.observations.restore')}
            pressed={rejected}
            busy={review.isPending}
            tone="danger"
            onClick={() => review.mutate({ reviewStatus: rejected ? 'unreviewed' : 'rejected' })}
          />
          <Button
            variant="ghost"
            className="min-h-9 px-3 py-1 text-sm"
            // Visibility ALONE. Sending a ruling with it would un-reject a
            // struck-through claim and put it back into the price, and would
            // mark an unreviewed low-confidence one as reviewed without
            // anybody having confirmed it — neither of which is what
            // "show the customer" asks for.
            onClick={() =>
              review.mutate({
                visibility: observation.visibility === 'customer' ? 'internal' : 'customer',
              })
            }
          >
            {observation.visibility === 'customer'
              ? t('magicQuote.observations.makeInternal')
              : t('magicQuote.observations.makeCustomer')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
